import { PeriscopeApexClass } from '../apex-classes';
import { PeriscopeApexTrigger } from '../apex-triggers';
import { PeriscopeFieldUpdate } from '../field-updates';
import { PeriscopeFlow } from '../flows';
import { PeriscopeFormulaField } from '../formula-fields';
import { PeriscopePageLayout } from '../page-layouts';
import { PeriscopeProcessBuilder } from '../process-builders';
import { PeriscopeValidationRule } from '../validation-rules';
import { PeriscopeWorkflowRule } from '../workflow-rules';
import { SonarRecord } from '@ui/records';
import { usePeriscopeActions } from '@hooks';
import {
	PeriscopeSfApexClass,
	PeriscopeSfEntityAutomation,
	PeriscopeSfFieldUpdate,
	PeriscopeSfFlow,
	PeriscopeSfFormulaField,
	PeriscopeSfPageLayout,
	PeriscopeSfProcessBuilder,
	PeriscopeSfValidationRule,
	PeriscopeSfWorkflowRule,
	SonarObjectType
} from '@sonar-software/sonar-ts-types';
import React, { useCallback } from 'react';

export interface PeriscopeAutomationProps {
	automation: PeriscopeSfEntityAutomation;
	parentIndex: number;
	showType?: boolean;
}

const renderAutomation = (
	automation: PeriscopeSfEntityAutomation,
	parentIndex: number,
	onClick: (automation: Omit<PeriscopeSfEntityAutomation, 'sonarObjectType'>) => void,
	showType?: boolean
) => {
	const { sonarObjectType, ...periscope } = automation;

	switch (sonarObjectType) {
	case SonarObjectType.SalesforceApexClass:
		return (
			<PeriscopeApexClass
				apex={ periscope as PeriscopeSfApexClass }
				key={ periscope.sonarObjectId }
				onApexClick={ onClick }
				showType={ showType }
				sonarObjectType={ sonarObjectType } />
		);
	case SonarObjectType.SalesforceApexTrigger:
		return (
			<PeriscopeApexTrigger
				apex={ periscope as PeriscopeSfApexClass }
				key={ periscope.sonarObjectId }
				onApexClick={ onClick }
				showType={ showType }
				sonarObjectType={ sonarObjectType } />
		);
	case SonarObjectType.SalesforceField:
		return (
			<PeriscopeFormulaField
				formulaField={ periscope as PeriscopeSfFormulaField }
				key={ periscope.sonarObjectId }
				onFormulaFieldClick={ onClick }
				showType={ showType } />
		);
	case SonarObjectType.SalesforceFieldUpdate:
		return (
			<PeriscopeFieldUpdate
				key={ periscope.sonarObjectId }
				onClick={ onClick }
				record={ periscope as PeriscopeSfFieldUpdate }
				showType={ showType } />
		);
	case SonarObjectType.SalesforceFlow:
		return (
			<PeriscopeFlow
				flow={ periscope as PeriscopeSfFlow }
				key={ periscope.sonarObjectId }
				onFlowClick={ onClick }
				showType={ showType } />
		);
	case SonarObjectType.SalesforcePageLayout:
		return (
			<PeriscopePageLayout
				item={ periscope as PeriscopeSfPageLayout }
				key={ periscope.sonarObjectId }
				onClick={ onClick }
				showType={ showType } />
		);
	case SonarObjectType.SalesforceProcessBuilder:
		return (
			<PeriscopeProcessBuilder
				builder={ periscope as PeriscopeSfProcessBuilder }
				key={ periscope.sonarObjectId }
				onBuilderClick={ onClick }
				showType={ showType } />
		);
	case SonarObjectType.SalesforceValidationRule:
		return (
			<PeriscopeValidationRule
				index={ parentIndex }
				key={ periscope.sonarObjectId }
				onRuleClick={ onClick }
				rule={ periscope as PeriscopeSfValidationRule }
				showType={ showType } />
		);
	case SonarObjectType.SalesforceWorkflowRule:
		return (
			<PeriscopeWorkflowRule
				index={ parentIndex }
				key={ periscope.sonarObjectId }
				onRuleClick={ onClick }
				rule={ periscope as PeriscopeSfWorkflowRule }
				showType={ showType } />
		);
	default:
		return (
			<SonarRecord
				isActive={ periscope.isActive }
				key={ periscope.sonarObjectId }
				name={ periscope.displayName }
				onClick={ () => onClick(periscope) }
				showType={ showType }
				sonarObjectId={ periscope.sonarObjectId }
				sonarObjectType={ sonarObjectType } />
		);
	}
};

export const PeriscopeAutomation: React.FC<PeriscopeAutomationProps> = ({ automation, parentIndex, showType }) => {
	const { openPeriscope } = usePeriscopeActions();

	const handleAutomationClick = useCallback(
		(sonarObjectType: SonarObjectType) =>
			(clickedAutomation: Omit<PeriscopeSfEntityAutomation, 'sonarObjectType'>) => {
				const { displayName, sonarObjectId } = clickedAutomation;

				openPeriscope({
					id: sonarObjectId,
					name: displayName,
					openedFromIndex: parentIndex,
					type: sonarObjectType
				});
			},
		[ openPeriscope, parentIndex ]
	);

	const { sonarObjectType } = automation;
	const onClick = handleAutomationClick(sonarObjectType);

	return renderAutomation(automation, parentIndex, onClick, showType);
};
